import { Validation } from '../../sceneDataSchema';
import { z } from 'zod';
import { ValidationType } from '@/constants/validationTypes';

export function stringValidator(validations: Validation[]) {
  const schema = validations.reduce(
    (schema, validation) => {
      switch (validation.type) {
        case ValidationType.Min:
          if (typeof validation.value === 'string') {
            return schema;
          }
          return schema.min(validation.value, `Must be at least ${validation.value} characters`);
        case ValidationType.Max:
          if (typeof validation.value === 'string') {
            return schema;
          }
          return schema.max(validation.value, `Must be at most ${validation.value} characters`);
        case ValidationType.Regex:
          return schema.regex(new RegExp(validation.value), validation.message ?? 'Invalid format');
        default:
          return schema;
      }
    },
    z.string({ message: 'this field is required' })
  );

  // First pass: check if field is required
  const required = validations.some(({ type }) => type === ValidationType.Required);

  const refinedSchema = validations.reduce((schema, validation): z.ZodString => {
    switch (validation.type) {
      case ValidationType.AllowedValues:
        return schema.refine(
          (val) => {
            // Allow empty values if field is not required
            if (!required && (!val || val.trim() === '')) {
              return true;
            }
            return validation.value.includes(val);
          },
          `Must be one of: ${validation.value.join(', ')}`
        );
      case ValidationType.NotAllowedValues:
        return schema.refine(
          (val) => {
            // Allow empty values if field is not required
            if (!required && (!val || val.trim() === '')) {
              return true;
            }
            return !validation.value.includes(val);
          },
          `Cannot be one of: ${validation.value.join(', ')}`
        );
      case ValidationType.Required:
        return schema.refine((val) => val.trim() !== '', 'Field is required');
      default:
        return schema;
    }
  }, schema);

  if (required) {
    return refinedSchema;
  }

  return refinedSchema.nullable();
}
